// Console.fm - Audio Player with Howler.js
import "@hotwired/turbo-rails";
import { Application } from "@hotwired/stimulus";
import { Howl, Howler } from "howler";
import ShellController from "./controllers/shell_controller";

const application = Application.start();
application.register("shell", ShellController);

// Audio Player Controller using Howler.js
class AudioPlayer {
  constructor() {
    this.sound = null;
    this.playlist = [];
    this.currentIndex = -1;
    this.currentTrack = null;
    this.isPlaying = false;
    this.isRepeat = false;
    this.isShuffle = false;
    this.seekInterval = null;

    this.restoreState();
    console.log("AudioPlayer initialized");
  }

  loadTrack(trackData) {
    console.log("Loading track:", trackData.title, trackData.stream);

    if (!trackData.stream) {
      console.warn("No stream URL for track:", trackData.title);
      return;
    }

    // Stop and unload current sound
    if (this.sound) {
      this.sound.unload();
      this.clearSeekInterval();
    }

    // Create new Howl instance
    this.sound = new Howl({
      src: [trackData.stream],
      html5: true, // Use HTML5 Audio for streaming large files
      preload: true,
      onload: () => {
        console.log("Track loaded:", trackData.title);
        this.updateDuration();
      },
      onplay: () => {
        console.log("Playing:", trackData.title);
        this.isPlaying = true;
        this.updatePlayPauseUI();
        this.startSeekInterval();
      },
      onpause: () => {
        console.log("Paused");
        this.isPlaying = false;
        this.updatePlayPauseUI();
        this.clearSeekInterval();
      },
      onstop: () => {
        this.isPlaying = false;
        this.updatePlayPauseUI();
        this.clearSeekInterval();
      },
      onend: () => {
        console.log("Track ended");
        this.clearSeekInterval();
        if (this.isRepeat) {
          this.sound.seek(0);
          this.sound.play();
        } else {
          this.next();
        }
      },
      onloaderror: (id, error) => {
        console.error("Load error:", error);
      },
      onplayerror: (id, error) => {
        console.warn("Play needs user audio unlock:", error);
        // Try to unlock audio on mobile
        this.sound.once("unlock", () => {
          this.sound.play();
        });
      },
    });

    // Update player UI
    this.currentTrack = trackData;
    this.updatePlayerUI(trackData);
    this.saveState();
  }

  startSeekInterval() {
    this.clearSeekInterval();
    this.seekInterval = setInterval(() => {
      this.updateProgress();
    }, 250);
  }

  clearSeekInterval() {
    if (this.seekInterval) {
      clearInterval(this.seekInterval);
      this.seekInterval = null;
    }
  }

  updateProgress() {
    if (!this.sound) return;

    const seek = this.sound.seek() || 0;
    const duration = this.sound.duration() || 1;
    const progress = (seek / duration) * 100;

    const progressEl = document.getElementById("progress");
    if (progressEl) {
      progressEl.style.width = `${progress}%`;
    }

    // Update time display if we add one
    this.saveState();
  }

  updateDuration() {
    if (!this.sound) return;
    const duration = this.sound.duration();
    console.log("Duration:", this.formatTime(duration));
  }

  updatePlayerUI(trackData) {
    const titleEl = document.getElementById("player-title");
    const artistEl = document.getElementById("player-artist");
    const artEl = document.getElementById("player-art");
    const progressEl = document.getElementById("progress");

    if (titleEl) titleEl.textContent = trackData.title || "Unknown Track";
    if (artistEl) artistEl.textContent = trackData.artist || "Unknown Artist";
    if (artEl && trackData.image) {
      artEl.style.backgroundImage = `url('${trackData.image}')`;
    }
    if (progressEl) progressEl.style.width = "0%";
  }

  updatePlayPauseUI() {
    const playBtn = document.getElementById("play");
    const pauseBtn = document.getElementById("pause");

    if (playBtn && pauseBtn) {
      playBtn.style.display = this.isPlaying ? "none" : "flex";
      pauseBtn.style.display = this.isPlaying ? "flex" : "none";
    }
  }

  play() {
    if (this.sound) {
      this.sound.play();
    } else if (this.currentTrack) {
      this.loadTrack(this.currentTrack);
      this.sound.play();
    }
  }

  pause() {
    if (this.sound) {
      this.sound.pause();
    }
  }

  toggle() {
    if (this.isPlaying) {
      this.pause();
    } else {
      this.play();
    }
  }

  seek(percent) {
    if (this.sound) {
      const duration = this.sound.duration();
      const seekTime = (percent / 100) * duration;
      this.sound.seek(seekTime);
      this.updateProgress();
    }
  }

  seekRelative(seconds) {
    if (this.sound) {
      const current = this.sound.seek() || 0;
      const duration = this.sound.duration() || 0;
      const newTime = Math.max(0, Math.min(duration, current + seconds));
      this.sound.seek(newTime);
      this.updateProgress();
    }
  }

  next() {
    if (this.playlist.length === 0) return;

    let nextIndex;
    if (this.isShuffle) {
      nextIndex = Math.floor(Math.random() * this.playlist.length);
    } else {
      nextIndex = (this.currentIndex + 1) % this.playlist.length;
    }

    this.currentIndex = nextIndex;
    const track = this.playlist[nextIndex];
    this.loadTrack(track);
    this.play();
    this.highlightTrack(track.id);
  }

  previous() {
    if (this.playlist.length === 0) return;

    // If more than 3 seconds in, restart track
    if (this.sound && this.sound.seek() > 3) {
      this.sound.seek(0);
      return;
    }

    let prevIndex =
      (this.currentIndex - 1 + this.playlist.length) % this.playlist.length;
    this.currentIndex = prevIndex;
    const track = this.playlist[prevIndex];
    this.loadTrack(track);
    this.play();
    this.highlightTrack(track.id);
  }

  toggleRepeat() {
    this.isRepeat = !this.isRepeat;
    const btn = document.getElementById("repeat");
    if (btn) {
      btn.classList.toggle("active", this.isRepeat);
    }
    this.saveState();
  }

  toggleShuffle() {
    this.isShuffle = !this.isShuffle;
    const btn = document.getElementById("shuffle");
    if (btn) {
      btn.classList.toggle("active", this.isShuffle);
    }
    this.saveState();
  }

  setPlaylist(tracks) {
    this.playlist = tracks;
  }

  highlightTrack(trackId) {
    document.querySelectorAll("[data-track-id]").forEach((el) => {
      el.classList.remove("active-track");
      if (el.dataset.trackId === String(trackId)) {
        el.classList.add("active-track");
      }
    });
  }

  saveState() {
    const state = {
      currentTrack: this.currentTrack,
      currentTime: this.sound ? this.sound.seek() : 0,
      isRepeat: this.isRepeat,
      isShuffle: this.isShuffle,
    };
    try {
      sessionStorage.setItem("playerState", JSON.stringify(state));
    } catch (e) {
      // Ignore storage errors
    }
  }

  restoreState() {
    try {
      const state = JSON.parse(sessionStorage.getItem("playerState"));
      if (state) {
        this.isRepeat = state.isRepeat || false;
        this.isShuffle = state.isShuffle || false;

        // Update UI toggles
        document
          .getElementById("repeat")
          ?.classList.toggle("active", this.isRepeat);
        document
          .getElementById("shuffle")
          ?.classList.toggle("active", this.isShuffle);

        if (state.currentTrack) {
          this.currentTrack = state.currentTrack;
          this.updatePlayerUI(state.currentTrack);
        }
      }
    } catch (e) {
      console.warn("Could not restore player state:", e);
    }
  }

  formatTime(seconds) {
    if (!seconds || isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  }

  // Volume control (0.0 to 1.0)
  setVolume(vol) {
    Howler.volume(vol);
  }

  getVolume() {
    return Howler.volume();
  }
}

// Initialize player globally
window.audioPlayer = new AudioPlayer();

// Build playlist from current page tracks
function buildPlaylist() {
  const tracks = [];
  document.querySelectorAll("[data-track-id]").forEach((el, index) => {
    tracks.push({
      id: el.dataset.trackId,
      title: el.dataset.trackTitle,
      artist: el.dataset.trackArtist,
      image: el.dataset.trackImage,
      stream: el.dataset.trackStream,
      index: index,
    });
  });
  return tracks;
}

// Event handler for track clicks
function handleTrackClick(e) {
  // Don't handle if clicking on a link inside the track
  if (e.target.closest("a")) return;

  const trackEl = e.target.closest("[data-track-id]");
  if (!trackEl) return;

  e.preventDefault();
  e.stopPropagation();

  const trackData = {
    id: trackEl.dataset.trackId,
    title: trackEl.dataset.trackTitle,
    artist: trackEl.dataset.trackArtist,
    image: trackEl.dataset.trackImage,
    stream: trackEl.dataset.trackStream,
  };

  console.log("Track clicked:", trackData.title);

  // Build playlist and find index
  const playlist = buildPlaylist();
  window.audioPlayer.setPlaylist(playlist);
  window.audioPlayer.currentIndex = playlist.findIndex(
    (t) => t.id === trackData.id
  );

  // Load and play
  window.audioPlayer.loadTrack(trackData);
  window.audioPlayer.play();
  window.audioPlayer.highlightTrack(trackData.id);
}

// Progress bar click to seek
function handleProgressClick(e) {
  const bar = e.target.closest(".statusbar");
  if (!bar) return;

  const rect = bar.getBoundingClientRect();
  const percent = ((e.clientX - rect.left) / rect.width) * 100;
  window.audioPlayer.seek(percent);
}

// Initialize event listeners
function initPlayerEvents() {
  console.log("Initializing player events");
  // Rebuild playlist for current page
  const playlist = buildPlaylist();
  if (playlist.length > 0) {
    window.audioPlayer.setPlaylist(playlist);
    console.log("Playlist loaded:", playlist.length, "tracks");
  }
}

document.addEventListener("DOMContentLoaded", initPlayerEvents);
document.addEventListener("turbo:load", initPlayerEvents);

// Global click handlers (delegated) - must be added only once
if (!window._playerClickHandlerAdded) {
  window._playerClickHandlerAdded = true;

  document.addEventListener(
    "click",
    (e) => {
      // Track selection - check this first with highest priority
      const trackEl = e.target.closest("[data-track-id]");
      if (trackEl && !e.target.closest("a")) {
        handleTrackClick(e);
        return;
      }

      // Progress bar seek
      if (e.target.closest(".statusbar")) {
        handleProgressClick(e);
        return;
      }

      // Play button
      if (e.target.closest("#play")) {
        e.preventDefault();
        window.audioPlayer.play();
        return;
      }

      // Pause button
      if (e.target.closest("#pause")) {
        e.preventDefault();
        window.audioPlayer.pause();
        return;
      }

      // Previous button
      if (e.target.closest("#previous")) {
        e.preventDefault();
        window.audioPlayer.previous();
        return;
      }

      // Next button
      if (e.target.closest("#next")) {
        e.preventDefault();
        window.audioPlayer.next();
        return;
      }

      // Repeat button
      if (e.target.closest("#repeat")) {
        e.preventDefault();
        window.audioPlayer.toggleRepeat();
        return;
      }

      // Shuffle button
      if (e.target.closest("#shuffle")) {
        e.preventDefault();
        window.audioPlayer.toggleShuffle();
        return;
      }
    },
    true
  ); // Use capture phase to get events before other handlers

  // Keyboard shortcuts
  document.addEventListener("keydown", (e) => {
    // Don't handle if typing in input
    if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;

    switch (e.code) {
      case "Space":
        e.preventDefault();
        window.audioPlayer.toggle();
        break;
      case "ArrowRight":
        e.preventDefault();
        if (e.shiftKey) {
          window.audioPlayer.next();
        } else {
          window.audioPlayer.seekRelative(10); // Skip 10 seconds
        }
        break;
      case "ArrowLeft":
        e.preventDefault();
        if (e.shiftKey) {
          window.audioPlayer.previous();
        } else {
          window.audioPlayer.seekRelative(-10); // Rewind 10 seconds
        }
        break;
      case "ArrowUp":
        e.preventDefault();
        window.audioPlayer.setVolume(
          Math.min(1, window.audioPlayer.getVolume() + 0.1)
        );
        break;
      case "ArrowDown":
        e.preventDefault();
        window.audioPlayer.setVolume(
          Math.max(0, window.audioPlayer.getVolume() - 0.1)
        );
        break;
    }
  });
}

console.log("Console.fm Audio Player ready (Howler.js)");
